@import '../../../../style/src/typography/mixins';
@import '../../mixins/outline';
@import '../../../../design-tokens/dist/private/colors';
@import '../../../../design-tokens/dist/private/gradients';
@import '../../../../style/lib/scss';

:host {
	@include zds-typography('body');

	border-radius: var(--zds-card-border-radius);
	display: flex;

	// Utility variables
	--zds-card-header-start-end-icon-color: var(--zds-card-header-color);
	--zds-card-header-start-end-icon-color-hover: var(--zds-card-header-color-hover);
}

::slotted([slot='status']) {
	background-color: var(--zds-card-status-background);
	color: var(--zds-card-status-color);
	font-size: var(--zds-font-size-xxs);
	font-weight: var(--zds-typography-headline-font-weight);
	padding: var(--zds-space-2) var(--zds-card-body-inset);
	text-align: var(--zds-card-status-text-align);
	text-transform: var(--zds-card-status-text-transform);

	@include zds-media-breakpoint-up('sm') {
		font-size: var(--zds-font-size-xs);
	}
}

::slotted([slot='media-overlay']) {
	position: absolute;
}

::slotted([slot='body']) {
	color: var(--zds-card-color);
}

::slotted([slot='preheader']) {
	@include zds-typography('subheadline-sm');

	color: var(--zds-card-preheader-color);
	padding-bottom: var(--zds-space-2);
}

::slotted([slot='header']) {
	@include zds-typography('headline-md');

	color: var(--zds-card-header-color);
	font-size: var(--zds-card-header-font-size);
	font-weight: var(--zds-card-header-font-weight);
	transition: var(--zds-card-transition);
}

::slotted([slot='subheader']) {
	@include zds-typography('subheadline-sm');

	color: var(--zds-card-subheader-color);
}

::slotted(zds-link-cta[slot='footer']) {
	flex: 1;
}

// Sets all possible card backgrounds based on the colors that exist within the design tokens.
@each $color in $zdsColors {
	:host([color='#{$color}']) {
		--zds-card-background: var(--zds-color-#{$color});
		--zds-card-color: var(--zds-color-#{$color}-foreground);
		--zds-card-icon-color: var(--zds-color-#{$color}-foreground);
		--zds-card-icon-color-hover: var(--zds-color-#{$color}-foreground-hover);
		--zds-card-header-color: var(--zds-color-#{$color}-foreground);
		--zds-card-header-color-hover: var(--zds-color-#{$color}-foreground-hover);
		--zds-card-subheader-color: var(--zds-color-#{$color}-foreground);
		--zds-link-color: var(--zds-color-#{$color}-foreground);
		--zds-link-color-hover: var(--zds-color-#{$color}-foreground-hover);
		--zds-link-cta-color: var(--zds-color-#{$color}-foreground);
		--zds-link-cta-color-hover: var(--zds-color-#{$color}-foreground-hover);
		--zds-card-header-start-end-icon-color: var(--zds-color-#{$color}-foreground);
		--zds-card-header-start-end-icon-color-hover: var(--zds-color-#{$color}-foreground-hover);
	}
}

// Sets all possible card backgrounds based on the gradients that exist within the design tokens.
@each $gradient in $zdsGradients {
	:host([color='#{$gradient}']) {
		--zds-card-background: linear-gradient(var(--zds-#{$gradient}-from), var(--zds-#{$gradient}-to));
		--zds-card-color: var(--zds-#{$gradient}-foreground);
		--zds-card-icon-color: var(--zds-#{$gradient}-foreground);
		--zds-card-icon-color-hover: var(--zds-#{$gradient}-foreground-hover);
		--zds-card-header-color: var(--zds-#{$gradient}-foreground);
		--zds-card-header-color-hover: var(--zds-#{$gradient}-foreground-hover);
		--zds-card-subheader-color: var(--zds-#{$gradient}-foreground);
		--zds-link-color: var(--zds-#{$gradient}-foreground);
		--zds-link-color-hover: var(--zds-#{$gradient}-foreground-hover);
		--zds-link-cta-color: var(--zds-#{$gradient}-foreground);
		--zds-link-cta-color-hover: var(--zds-#{$gradient}-foreground-hover);
		--zds-card-header-start-end-icon-color: var(--zds-#{$gradient}-foreground);
		--zds-card-header-start-end-icon-color-hover: var(--zds-#{$gradient}-foreground-hover);
	}
}

.zds-card {
	$this: &;

	@include outline;

	background: var(--zds-card-background);
	border-color: var(--zds-card-border-color);
	border-radius: var(--zds-card-border-radius);
	border-style: solid;
	border-width: var(--zds-card-border-width);
	box-shadow: var(--zds-card-box-shadow);
	color: inherit;
	display: flex;
	flex: auto;
	flex-direction: column;
	height: inherit;
	min-height: inherit;
	overflow: hidden;
	text-decoration: none;
	transition:
		border-color var(--zds-card-transition),
		box-shadow var(--zds-card-transition); // NB: focus outline should not get transition

	word-break: break-word;

	&__container {
		display: flex;
		flex-direction: column;
		flex-grow: 1;
	}

	&__media-container {
		border-radius: var(--zds-card-media-border-radius);
		display: flex;
		flex-direction: column;
		flex-shrink: 0;
		overflow: hidden;
		position: relative;

		a {
			cursor: pointer;
		}
	}

	&__media-link {
		@include outline;

		--zds-outline-offset: -3px;
	}

	&__media {
		align-items: center;
		display: flex;
		flex-grow: 1;
		justify-content: center;
		line-height: 0;
		transition: var(--zds-motion-image-hover-transition);

		a:hover & {
			transform: var(--zds-motion-image-hover-transform);
		}

		&--fit-cover ::slotted([slot='media']) {
			object-fit: cover;
		}

		&--fit-contain ::slotted([slot='media']) {
			object-fit: contain;
		}
	}

	&__media-overlay {
		display: flex;
		flex-direction: column;
		inset: 0;
		pointer-events: none;
		position: absolute;

		&__content {
			flex-grow: 1;
			position: relative;
		}
	}

	&__status {
		z-index: 1;
	}

	&__main,
	&__footer {
		padding: var(--zds-card-body-inset);
	}

	&--no-body-inset#{&}--vertical &__main,
	&--no-body-inset#{&}--vertical &__footer {
		padding-bottom: 0;
		padding-inline: 0;
	}

	&--no-body-inset#{&}--vertical &__footer {
		margin-top: var(--zds-card-body-inset);
	}

	&--no-body-inset#{&}--horizontal &__main {
		padding-block: 0;
	}

	&--no-body-inset#{&}--horizontal &__footer {
		padding-bottom: 0;
	}

	&--no-body-inset#{&}--horizontal:not(&--mirror) &__main,
	&--no-body-inset#{&}--horizontal:not(&--mirror) &__footer {
		padding-right: 0;
	}

	&--no-body-inset#{&}--horizontal#{&}--mirror &__main,
	&--no-body-inset#{&}--horizontal#{&}--mirror &__footer {
		padding-left: 0;
	}

	&__main {
		display: flex;
		flex-direction: column;
		flex-grow: 1;
		gap: var(--zds-card-body-inset);
	}

	&__icon {
		color: var(--zds-card-icon-color);
		display: flex;
		transition: var(--zds-card-transition);
	}

	&__header-wrapper {
		align-items: flex-start;
		display: flex;
		margin: 0 calc(-1 * var(--zds-space-2));
	}

	&__header-main {
		flex-grow: 1;
		max-width: 100%;
		min-width: 0;
		padding: 0 var(--zds-space-2);
	}

	&__header-start,
	&__header-end {
		align-items: center;
		display: flex;
		flex-shrink: 0;

		::slotted(*) {
			color: var(--zds-card-header-start-end-icon-color);
			margin: 0 var(--zds-space-2);
			transition: var(--zds-card-transition);
		}
	}

	&__body {
		display: flex;
		flex-direction: column;
		flex-grow: 1;
	}

	&__footer {
		border-top: var(--zds-card-footer-border-width) solid var(--zds-card-footer-border-color);

		&--custom-height {
			align-items: center;
			display: flex;
			padding-block: 0;
		}
	}

	&--alignment {
		&-start {
			.zds-card__icon {
				justify-content: start;
			}

			.zds-card__header-main,
			.zds-card__body {
				text-align: start;
			}
		}

		&-center {
			.zds-card__icon {
				justify-content: center;
			}

			.zds-card__header-main,
			.zds-card__body {
				text-align: center;
			}
		}

		&-end {
			.zds-card__icon {
				justify-content: end;
			}

			.zds-card__header-main,
			.zds-card__body {
				text-align: end;
			}
		}
	}

	// Vertical card image sizing
	::slotted(img[slot='media']) {
		margin-right: -1px; // Fix bug where object-fit cover leaves 1px of space
		width: calc(100% + 1px);
	}

	// Horizontal card image sizing
	&--horizontal {
		display: flex;
		flex-direction: row;

		&.zds-card--mirror {
			flex-direction: row-reverse;
		}

		::slotted(img[slot='media']) {
			min-height: 100%;
			min-width: 100%;
		}

		@each $breakpoint in map-keys($zds-breakpoints) {
			@include zds-media-breakpoint-up($breakpoint) {
				&-#{$breakpoint}-up ::slotted(img[slot='media']) {
					min-height: 100%;
					min-width: 100%;
				}
			}

			@include zds-media-breakpoint-down($breakpoint) {
				&-#{$breakpoint}-down ::slotted(img[slot='media']) {
					min-height: 100%;
					min-width: 100%;
				}
			}
		}
	}

	// Horizontal card image sizing for ratio cards
	&--has-ratio.zds-card--horizontal {
		::slotted(img[slot='media']) {
			position: absolute;
		}

		.zds-card__container {
			overflow: hidden;
		}

		@each $breakpoint in map-keys($zds-breakpoints) {
			@include zds-media-breakpoint-up($breakpoint) {
				&-#{$breakpoint}-up {
					::slotted(img[slot='media']) {
						position: absolute;
					}

					.zds-card__container {
						overflow: hidden;
					}
				}
			}

			@include zds-media-breakpoint-down($breakpoint) {
				&-#{$breakpoint}-down {
					::slotted(img[slot='media']) {
						position: absolute;
					}

					.zds-card__container {
						overflow: hidden;
					}
				}
			}
		}
	}

	// Revert any inline ratio changes when not applicable
	&--has-ratio {
		&.zds-card--vertical .zds-card__container {
			flex: 0 !important;
		}

		@each $breakpoint in map-keys($zds-breakpoints) {
			@include zds-media-breakpoint-down($breakpoint) {
				&.zds-card--horizontal-#{--zds-breakpoint-next($breakpoint)} .zds-card__container {
					flex: 0 !important;
				}
			}
		}
	}
}

a.zds-card {
	cursor: pointer;

	&:hover {
		border-color: var(--zds-card-border-color-hover);
		box-shadow: var(--zds-card-box-shadow-hover);

		.zds-card__icon {
			color: var(--zds-card-icon-color-hover);
			transform: var(--zds-card-icon-transform-hover);
		}

		.zds-card__header-start,
		.zds-card__header-end {
			::slotted(*) {
				color: var(--zds-card-header-start-end-icon-color-hover);
			}
		}

		::slotted([slot='header']) {
			color: var(--zds-card-header-color-hover);
		}
	}
}

@each $breakpoint in map-keys($zds-breakpoints) {
	@include zds-media-breakpoint-up($breakpoint) {
		.zds-card--horizontal-#{$breakpoint}-up {
			display: flex;
			flex-direction: row;

			&.zds-card--mirror {
				flex-direction: row-reverse;
			}
		}
	}

	@include zds-media-breakpoint-down($breakpoint) {
		.zds-card--horizontal-#{$breakpoint}-down {
			display: flex;
			flex-direction: row;

			&.zds-card--mirror {
				flex-direction: row-reverse;
			}
		}
	}
}
